import { AppController } from 'app/controllers';
import { AppViewModel } from '..';
import { ValueModel } from '@zajno/common-mobx/viewModels';
import * as Yup from 'yup';
import { CampaignStatus } from 'common/types/models/campaign';
import { EnumStringHelper } from '@zajno/common/structures/helpers/enum';

enum LocalStorageKeys {
    Filters = 'valley-filters',
    Sort = 'valley-sorting',
}

export enum CampaignSortBy {
    Ascending = 'Oldest first',
    Descending = 'Newest first',
}

export namespace CampaignSortBy {
    export const Helper = new EnumStringHelper<CampaignSortBy>(CampaignSortBy);
}

const initialPage = 0;
const defaultLimit = 20;

export type SearchData = {
    searchOrder: string
};

export const campaignSearchValidationSchema: Yup.ObjectSchema<SearchData> = Yup.object().shape({
    searchOrder: Yup.string(),
});

export class CampaignsPageViewModel {
    public _searchString = new ValueModel(null);
    public activeFiltersModel = new ValueModel<CampaignStatus[]>([]);
    public sortByModel = new ValueModel<CampaignSortBy>(CampaignSortBy.Descending);

    private _loadMoreModel = AppViewModel.Instance.loadMoreCampaignsModel;

    constructor() {
        this._loadMoreModel.initialize(
            initialPage,
            defaultLimit,
            this._getCampaignsByRole,
        );

        this._resetFilterModels();
    }

    get isLoading() { return this._loadMoreModel.isLoading && AppController.Instance.Campaigns.isLoading; }
    get campaigns() { return this._loadMoreModel.items; }

    get hasMore() { return this._loadMoreModel.hasMore; }

    get userRole() { return AppViewModel.Instance.userRole.role; }
    get isAdmin() { return AppViewModel.Instance.userRole.isAdmin; }

    public setSearchString = (v: string) => {
        this._searchString.setValue(v);

        this._loadMoreModel.reset();
    };

    public setFilter = (status: CampaignStatus) => {
        if (this.activeFiltersModel.value.includes(status)) {
            this.activeFiltersModel.setValue(this.activeFiltersModel.value.filter(item => item !== status));
        } else {
            this.activeFiltersModel.setValue([...this.activeFiltersModel.value, status]);
        }

        localStorage.setItem(LocalStorageKeys.Filters, JSON.stringify(this.activeFiltersModel.value));

        this._loadMoreModel.reset();
    };

    public setSortByMethod = (method: CampaignSortBy) => {
        this.sortByModel.setValue(method);

        localStorage.setItem(LocalStorageKeys.Sort, JSON.stringify(method));

        this._loadMoreModel.reset();
    };

    public handleClickLoadMore = () => { this._loadMoreModel.loadMore(); };

    private _getCampaignsByRole = async (offset: number, limit: number) => {
        if (AppViewModel.Instance.userRole.isAdmin) {
            this._resetFilterModels();

            return AppController.Instance.Campaigns.getCampaigns(
                offset,
                limit,
                this._searchString.value,
                this.activeFiltersModel.value,
                this.sortByModel.value === CampaignSortBy.Ascending,
            );
        }

        const userId = AppController.Instance.Auth.user.id;

        return AppController.Instance.Campaigns.getCampaignsByUser(userId, offset, limit);
    };

    private _resetFilterModels = () => {
        this.activeFiltersModel.setValue(
            JSON.parse(localStorage.getItem(LocalStorageKeys.Filters)) || [],
        );

        this.sortByModel.setValue(
            JSON.parse(localStorage.getItem(LocalStorageKeys.Sort)) || CampaignSortBy.Descending,
        );
    };
}
